import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { withLocalize } from 'react-localize-redux';
import toaster from '../utils/toaster';
import { useSlackAgent } from './slackAgentContext';
import SlackConnectControl from './components/SlackConnectControl';
import SlackLinkConfirmModal from './components/SlackLinkConfirmModal';
import {
    CONNECT_RESULTS,
    ENTRY_SURFACES,
    ERROR_PARAM,
    GENERIC_ERROR_KEY,
    INVITE_ERROR_KEYS,
    LINK_TOKEN_PARAM,
    RESULT_PARAM,
    SLACK_AGENT_EVENTS,
    SLACK_URL_PARAMS,
    TOAST_AUTO_CLOSE_MS,
    connectErrorToastKey,
    toastKey,
    trackSlackAgentEvent,
} from './constants';

const RESULT_TOAST_KEYS = {
    [CONNECT_RESULTS.INSTALLED]: 'WORKSPACE_CONNECTED',
    [CONNECT_RESULTS.LINKED]: 'ACCOUNT_LINKED',
};

const RESULT_EVENTS = {
    [CONNECT_RESULTS.INSTALLED]: SLACK_AGENT_EVENTS.WORKSPACE_INSTALLED,
    [CONNECT_RESULTS.LINKED]: SLACK_AGENT_EVENTS.ACCOUNT_LINKED,
};

const SlackConnect = ({ translate }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [pendingLink, setPendingLink] = useState(null);
    const { connection, needsWorkspace, connectNow, openConnect } = useSlackAgent();
    const { status, busy, refresh, linkAccount, unlink, openInvite, confirmInvite } = connection;

    const notify = useCallback(
        (notifier, key) => notifier(translate(key), { autoClose: TOAST_AUTO_CLOSE_MS }),
        [translate]
    );

    const showFailure = useCallback(key => notify(toaster.error, toastKey(key)), [notify]);

    const clearSlackParams = useCallback(() => {
        setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            SLACK_URL_PARAMS.forEach(param => next.delete(param));
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const openPendingLink = useCallback(async token => {
        try {
            setPendingLink(await openInvite(token));
            trackSlackAgentEvent(SLACK_AGENT_EVENTS.INVITE_OPENED);
        } catch (error) {
            showFailure(INVITE_ERROR_KEYS[error.status] ?? GENERIC_ERROR_KEY);
        }
    }, [openInvite, showFailure]);

    const consumedParamsRef = useRef(null);

    useEffect(() => {
        const result = searchParams.get(RESULT_PARAM);
        const errorCode = searchParams.get(ERROR_PARAM);
        const linkToken = searchParams.get(LINK_TOKEN_PARAM);
        if (!result && !errorCode && !linkToken) return;

        const consumed = `${result}|${errorCode}|${linkToken}`;
        if (consumedParamsRef.current === consumed) return;
        consumedParamsRef.current = consumed;
        // Cleared before anything async: these params are single use, so a reload must not retry one.
        clearSlackParams();

        if (result && RESULT_TOAST_KEYS[result]) {
            trackSlackAgentEvent(RESULT_EVENTS[result]);
            notify(toaster.success, toastKey(RESULT_TOAST_KEYS[result]));
            refresh();
        } else if (errorCode) {
            notify(toaster.error, connectErrorToastKey(errorCode));
        } else if (linkToken) {
            openPendingLink(linkToken);
        }
    }, [searchParams, clearSlackParams, notify, refresh, openPendingLink]);

    // A first install gets the explainer; a reconnect or update repairs a workspace the org already approved.
    const handleConnect = () =>
        needsWorkspace ? openConnect(ENTRY_SURFACES.HEADER) : connectNow(ENTRY_SURFACES.HEADER);

    const handleLink = async () => {
        trackSlackAgentEvent(SLACK_AGENT_EVENTS.LINK_CLICKED);
        try {
            await linkAccount();
        } catch {
            showFailure(GENERIC_ERROR_KEY);
        }
    };

    const handleUnlink = async () => {
        try {
            await unlink();
            trackSlackAgentEvent(SLACK_AGENT_EVENTS.ACCOUNT_UNLINKED);
            notify(toaster.success, toastKey('ACCOUNT_UNLINKED'));
        } catch {
            showFailure(GENERIC_ERROR_KEY);
        }
    };

    const handleConfirmLink = async () => {
        const token = pendingLink.confirm_token;
        try {
            const linked = await confirmInvite(token);
            setPendingLink(null);
            trackSlackAgentEvent(SLACK_AGENT_EVENTS.ACCOUNT_LINKED);
            // Telling someone to go and ask again reads as a failure when the answer is already coming.
            notify(toaster.success, toastKey(linked?.answering_pending_question
                ? 'ACCOUNT_LINKED_ANSWERING' : 'ACCOUNT_LINKED'));
        } catch (error) {
            setPendingLink(null);
            showFailure(INVITE_ERROR_KEYS[error.status] ?? GENERIC_ERROR_KEY);
        }
    };

    return (
        <>
            <SlackConnectControl
                status={status}
                busy={busy}
                onConnect={handleConnect}
                onLink={handleLink}
                onUnlink={handleUnlink}
            />
            <SlackLinkConfirmModal
                pendingLink={pendingLink}
                busy={busy}
                onAccept={handleConfirmLink}
                onReject={() => setPendingLink(null)}
            />
        </>
    );
};

SlackConnect.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(SlackConnect);
